import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send, Check, Clock, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fmtPhone } from "@/lib/format";

function SignerRow({ s }) {
  const done = s.status === "assinado";
  return (
    <li className="flex items-start gap-3 py-2.5" data-testid={`assinaja-signer-${s.key}`}>
      <span className={`mt-0.5 shrink-0 ${done ? "text-emerald-600" : "text-slate-400"}`}>{done ? <Check size={16} /> : <Clock size={16} />}</span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold navy-text">{s.name || "—"} <span className="font-normal text-slate-500">· {s.role}</span></div>
        <div className="text-xs text-slate-500 break-all">{s.email || <span className="text-red-700">sem email</span>} · {fmtPhone(s.phone) || <span className="text-red-700">sem telemóvel</span>}</div>
      </div>
      {s.status && <span className={`text-xs font-semibold shrink-0 ${done ? "text-emerald-700" : "text-amber-700"}`}>{done ? "Assinou" : "Falta assinar"}</span>}
    </li>
  );
}

// Confirmar signatários e enviar o CMI para a AssinaJá; depois de enviado, mostra o estado.
export function AssinajaDialog({ version, onClose, onSent }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/v1/documents/${version.id}/send-assinaja`).then(({ data }) => setD(data))
      .catch((e) => { toast.error(apiErr(e.response?.data?.detail)); onClose(); });
  }, [version.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const sent = ["enviado_assinaja", "assinado"].includes(d?.sign?.status);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/v1/documents/${version.id}/send-assinaja`, {});
      toast.success(d.demo ? "CMI enviado (simulação de demonstração: nenhum email sai)." : data.branded_email?.sent ? "CMI enviado para assinatura. Cada signatário recebe o email da agência com o seu link." : "CMI enviado para assinatura. Os signatários recebem o email da AssinaJá.");
      if (data.branded_email?.error) toast.warning(`O email com a marca da agência não foi enviado (${data.branded_email.error}). Segue só o convite da AssinaJá.`, { duration: 12000 });
      onSent();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const blocked = d && (d.lock || d.problems.length > 0 || (!d.demo && !d.configured));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-[600px] w-[96vw] max-h-[92vh] overflow-y-auto" data-testid="assinaja-dialog">
        <DialogHeader>
          <DialogTitle className="navy-text">{sent ? "Estado da assinatura" : "Enviar para assinatura"}</DialogTitle>
          <DialogDescription>Contrato de mediação v{version.n} · {sent ? `enviado a ${new Date(d.sign.sent_at).toLocaleDateString("pt-PT")} por ${d.sign.sent_by}` : "cada signatário recebe um email da AssinaJá e assina só com a Chave Móvel Digital (tem de ter a assinatura digital da CMD ativa)."}</DialogDescription>
        </DialogHeader>
        {!d ? <div className="py-12 grid place-items-center text-slate-400"><Loader2 className="animate-spin" /></div> : (
          <>
            {d.demo && !sent && <p className="text-sm rounded-lg bg-amber-50 text-amber-900 px-3 py-2" data-testid="assinaja-mock">Processo de demonstração: a AssinaJá não é chamada. As assinaturas são simuladas (uma a cada 25 segundos) e nenhum email sai.</p>}
            <ul className="divide-y divide-slate-100 border border-slate-100 rounded-xl px-3" data-testid="assinaja-signers">
              {d.signers.map((s) => <SignerRow key={s.key} s={s} />)}
            </ul>
            {!sent && (d.lock || d.problems.length > 0 || (!d.demo && !d.configured)) && (
              <div className="text-sm rounded-lg bg-red-50 text-red-800 px-3 py-2 space-y-0.5" data-testid="assinaja-problems">
                {d.lock && <p className="flex gap-1.5"><AlertCircle size={15} className="shrink-0 mt-0.5" />{d.lock}</p>}
                {d.problems.map((p) => <p key={p} className="flex gap-1.5"><AlertCircle size={15} className="shrink-0 mt-0.5" />{p}</p>)}
                {!d.demo && !d.configured && <p className="flex gap-1.5"><AlertCircle size={15} className="shrink-0 mt-0.5" />AssinaJá por configurar: falta a chave API.</p>}
                {d.problems.length > 0 && <p className="text-xs text-red-700/80 pt-1">Complete os contactos no processo e volte a abrir esta janela.</p>}
              </div>
            )}
            {!sent && <p className="text-xs text-slate-500" data-testid="assinaja-send-note">{d.demo ? "Simulação: as assinaturas aparecem sozinhas em cerca de 1 minuto." : "Ao enviar, a AssinaJá envia logo os emails. Este passo não se pode desfazer."}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="assinaja-cancel">{sent ? "Fechar" : "Cancelar"}</button>
              {!sent && (
                <button onClick={send} disabled={busy || blocked} className="h-10 px-4 inline-flex items-center gap-2 rounded-lg navy-bg text-white text-sm font-semibold disabled:opacity-50" data-testid="assinaja-confirm">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Enviar para assinatura
                </button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
